import React from 'react'
import { CopyPool, DynamicAsset, DynamicCombination } from './dynamicCombination'
import { accentSoft, dark, radius, type as typo } from '../tokens'

interface Props {
  assets: DynamicAsset[]
  pools: CopyPool[]
  at: DynamicCombination
  onPick: (next: DynamicCombination) => void
}

const eyebrowStyle: React.CSSProperties = {
  fontFamily: typo.family.sans,
  fontSize: typo.size.eyebrow,
  fontWeight: typo.weight.semibold,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: dark.textFaint,
}

const optionStyle = (selected: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  width: '100%',
  padding: '8px 10px',
  textAlign: 'left',
  borderRadius: radius.sm,
  border: `1px solid ${selected ? dark.accent : dark.hairline}`,
  background: selected ? accentSoft : dark.surface,
  cursor: 'pointer',
})

/** The chosen-one marker. A dot rather than a check: a check reads as approval. */
const dotStyle = (selected: boolean): React.CSSProperties => ({
  flexShrink: 0,
  marginTop: 4,
  width: 12,
  height: 12,
  borderRadius: '50%',
  border: `1px solid ${selected ? dark.accent : dark.textFaint}`,
  background: selected ? dark.accent : 'transparent',
})

/**
 * The copy and media a dynamic ad can be assembled from, as lists you read and
 * pick from.
 *
 * Every variant is on screen as text at once, because the reviewer is approving
 * all of them and can only do that having read them. Picking one redraws the ad
 * around it, which is the only way to judge whether a headline works against a
 * given image. Each pool is independent, so four can be held still while the
 * fifth moves.
 *
 * Deliberately not a counter with arrows: that shows one variant, hides the
 * rest, and makes reaching a particular one a matter of cycling past the others.
 *
 * Read-only for now. A variant row is a marker plus its text, so making copy
 * editable later is that text becoming a field rather than the list being rebuilt.
 */
export function DynamicPoolPicker({ assets, pools, at, onPick }: Props) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Without this a list of headlines with a selected one reads as "choose
        the headline you want", which is the opposite of what a dynamic ad does —
        and the client, unlike the AE in the importer, has no surrounding context
        to correct the impression. */}
      <p style={{ ...eyebrowStyle, textTransform: 'none', letterSpacing: 0, margin: 0 }}>
        This ad ships with every option below — Facebook mixes one of each when it runs. Pick any
        combination to see how it looks.
      </p>

      {assets.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={eyebrowStyle}>Media · {assets.length}</span>
          <div
            role="group"
            aria-label="Media"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
          >
            {assets.map((asset, index) => {
              const still = asset.kind === 'video' ? asset.thumbnail : asset.url
              const selected = index === at.asset
              return (
                <button
                  key={index}
                  type="button"
                  aria-pressed={selected}
                  aria-label={`Media ${index + 1}`}
                  onClick={() => onPick({ ...at, asset: index })}
                  style={{
                    width: 60,
                    height: 60,
                    padding: 0,
                    overflow: 'hidden',
                    borderRadius: radius.sm,
                    border: `2px solid ${selected ? dark.accent : dark.hairline}`,
                    background: dark.surface,
                    opacity: selected ? 1 : 0.65,
                    cursor: 'pointer',
                  }}
                >
                  {still && (
                    <img
                      src={still}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {pools.map(pool => (
        <div key={pool.key} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={eyebrowStyle}>
            {pool.label} · {pool.values.length}
          </span>
          <div
            role="group"
            aria-label={pool.label}
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
          >
            {pool.values.map((value, index) => {
              const selected = index === at[pool.key]
              return (
                <button
                  key={index}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onPick({ ...at, [pool.key]: index })}
                  style={optionStyle(selected)}
                >
                  <span style={dotStyle(selected)} />
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontFamily: typo.family.sans,
                      fontSize: typo.size.body,
                      lineHeight: 1.5,
                      color: selected ? dark.text : dark.textMuted,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {value || '—'}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
